import { useEffect, useRef, useState } from "react";
import { useInView, useMotionValueEvent, useReducedMotion, useScroll } from "framer-motion";
import { Bookmark, Compass, ShieldCheck, CalendarCheck } from "lucide-react";
import { ParallaxScene, ParallaxLayer } from "../ParallaxScene";
import { agentChips, agentConversation, personalFeatures } from "../../data/phase2Content";
import { useIsDesktop } from "../../hooks/useMedia";
import { PersonalAgentPreview } from "./PersonalAgentPreview";
import { Words } from "../Words";

const ICONS = { remembers: Bookmark, finds: Compass, acts: ShieldCheck };
const TOTAL = agentConversation.length;

const FeatureCard = ({ f }) => {
  const Icon = ICONS[f.id];
  return (
    <div className="glass glass-light rounded-[28px] p-6" data-testid={`personal-feature-${f.id}`}>
      <span className="grid h-10 w-10 place-items-center rounded-full bg-[#08090D] text-white"><Icon size={17} strokeWidth={1.8} aria-hidden /></span>
      <p className="mt-4 text-[22px] font-bold tracking-[-0.03em] text-[#08090D]">{f.title}</p>
      <p className="mt-1.5 text-[16px] text-[#4c5064]">{f.body}</p>
    </div>
  );
};

const FrontChips = () => (
  <>
    {agentChips.map((c) => (
      <div key={c.id} className="pointer-events-none absolute z-10" style={c.style}>
        <ParallaxLayer y={c.speed}>
          <div className="glass glass-heavy flex items-center gap-2 rounded-full py-2 pl-2 pr-4 text-[13px] font-semibold text-[#08090D]" data-testid={`agent-chip-${c.id}`}>
            {c.avatar ? <img src={c.avatar} alt="" className="h-7 w-7 rounded-full object-cover" /> : <span className="grid h-7 w-7 place-items-center rounded-full bg-[#A5E6E3]/60"><CalendarCheck size={14} aria-hidden /></span>}
            {c.text}
          </div>
        </ParallaxLayer>
      </div>
    ))}
  </>
);

const useScrollReveal = (ref, enabled, setRevealed) => {
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.55", "end 0.85"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    if (enabled) setRevealed((r) => Math.max(r, Math.min(TOTAL, Math.ceil(v * (TOTAL + 0.5)))));
  });
};

const useTimedReveal = (ref, enabled, reduce, setRevealed) => {
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });
  useEffect(() => {
    if (!enabled || !inView) return;
    if (reduce) return setRevealed(TOTAL);
    const t = setInterval(() => setRevealed((r) => (r >= TOTAL ? r : r + 1)), 1100);
    return () => clearInterval(t);
  }, [enabled, inView, reduce, setRevealed]);
};

export default function PersonalAISection() {
  const desktop = useIsDesktop();
  const reduce = useReducedMotion();
  const scrollDriven = desktop && !reduce;
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    if (reduce) setRevealed(TOTAL);
  }, [reduce]);
  const gridRef = useRef(null);
  const phoneRef = useRef(null);
  useScrollReveal(gridRef, scrollDriven, setRevealed);
  useTimedReveal(phoneRef, !scrollDriven, reduce, setRevealed);

  return (
    <ParallaxScene as="section" id="personal-ai" offset={["start end", "end start"]} data-testid="section-personal-ai" className="personal-stage relative pb-28 pt-28 lg:pb-40 lg:pt-40">
      <div ref={gridRef} className="relative mx-auto grid max-w-[1240px] gap-12 px-5 lg:grid-cols-[1fr_440px] lg:gap-16 lg:px-10 xl:gap-24">
        <div>
          <p className="eyebrow">06 · Your Personal AI</p>
          <h2 className="h-section mt-4 text-[#08090D]" data-testid="personal-ai-headline">
            <Words text="Your World." />
            <br />
            <Words text="Your AI." start={2} className="text-gradient" />
          </h2>
          <p className="mt-6 max-w-[520px] text-[18px] leading-relaxed text-[#4c5064] sm:text-[20px]">
            An intelligent personal assistant that understands your approved preferences and helps you turn intentions into experiences.
          </p>
          <div className="mt-12 space-y-5 lg:mt-24 lg:space-y-[34vh] lg:pb-[20vh]">
            {personalFeatures.map((f) => <FeatureCard key={f.id} f={f} />)}
          </div>
        </div>
        <div ref={phoneRef}>
          <div className="relative mx-auto flex w-full max-w-[440px] flex-col items-center lg:sticky lg:top-[96px]">
            <PersonalAgentPreview revealed={revealed} />
            {desktop && <FrontChips />}
            <p className="mt-4 text-[12px] text-[#777B8D]" data-testid="personal-ai-status">
              {revealed < TOTAL ? "Keep scrolling — the conversation continues" : "Try Explore, Join or Approve inside the phone"}
            </p>
          </div>
        </div>
      </div>
    </ParallaxScene>
  );
}
